import React, { useEffect, useState } from 'react';
import {
  Accordion,
  Container,
  AccordionSummary,
  AccordionDetails,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Divider,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import StockIssue from './StockIssue';
import StockReturnReport from './StockReturnReport';
import StockSummaryReport from './StockSummaryReport';
import VendorPurchaseReport from './VendorPurchaseReport';
import StockMovementReport from './StockMovementReport';
// import AdmReport from './AdmReport';

import { useParams } from "react-router-dom";
import base64 from "base64-js";

const reportInfoData = [
  {
    title: "Stock Issue Report",
    color: "#1976D2",
    description: "Tracks all inventory items that have been issued out from stock.",
    filters: ["Date Range (From Date – To Date)"],
    columns: ["Item Code", "Item Name", "Branch Name", "Issued Quantity", "Issue Date", "Issued To Type", "Issued To", "Return Status"],
    exports: ["PDF", "CSV"],
  },
  {
    title: "Stock Return Report",
    color: "#388E3C",
    description: "Displays records of inventory items that have been returned back to stock.",
    filters: ["Date Range (From Date – To Date)"],
    columns: ["Item Code", "Item Name", "Branch Name", "Returned Quantity", "Return Date", "Returned By Type", "Returned By"],
    exports: ["PDF", "CSV"],
  },
  {
    title: "Stock Summary Report",
    color: "#F57C00",
    description: "Provides a consolidated summary of current inventory status across all branches and categories.",
    filters: ["Category", "Date Range (From Date – To Date)"],
    columns: ["Branch Name", "Category Name", "Item Code", "Item Name", "Purchased Price", "Current Balance", "Current Value"],
    exports: ["PDF", "CSV"],
  },
  {
    title: "Stock Movement Report",
    color: "#7B1FA2",
    description: "Shows all inventory movements including transfers, adjustments, and transactions over a period.",
    filters: ["Date Range (From Date – To Date)"],
    columns: ["Item Code", "Item Name", "Branch Name", "Movement Type", "Quantity", "Movement Date", "Moved By Type", "Moved By"],
    exports: ["PDF", "CSV"],
  },
  {
    title: "Vendor Purchase Report",
    color: "#C62828",
    description: "Lists all purchases made from vendors within the selected date range.",
    filters: ["Vendor", "Date Range (From Date – To Date)"],
    columns: ["Vendor Name", "Item Code", "Item Name", "Purchased Price", "Purchase Date", "Current Balance"],
    exports: ["PDF", "CSV"],
  },
];

function InventoryReport() {
  const { encodedFormId } = useParams();
  const [infoOpen, setInfoOpen] = useState(false);

  const decodeFormId = (encodedFormId) => {
    const bytes = base64.toByteArray(encodedFormId);
    return new TextDecoder().decode(bytes);
  };

  const formId = decodeFormId(encodedFormId);

  useEffect(() => {
    console.log("Decoded formId for Inventory Report:", formId);
    // You can use formId for authorization or tracking
  }, [formId]);

  return (
    <Container>
      <AppBar position="static" style={{ backgroundColor: "#0B1F40" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold', flexGrow: 1 }}>
            Inventory Report
          </Typography>
          <IconButton
            color="inherit"
            onClick={() => setInfoOpen(true)}
            title="Report Information"
            style={{
              backgroundColor: "rgba(255,255,255,0.15)",
              border: "2px solid rgba(255,255,255,0.6)",
              borderRadius: "50%",
              padding: "6px",
            }}
          >
            <InfoIcon style={{ fontSize: 26 }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Info Dialog */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { borderRadius: 16, padding: 0 }
        }}
      >
        <DialogTitle
          style={{
            backgroundColor: "#0B1F40",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon style={{ marginRight: 8 }} />
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Inventory Reports — Overview
            </Typography>
          </Box>
         
        </DialogTitle>

        <DialogContent dividers style={{ backgroundColor: "#f9f9f9", padding: "24px" }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
            This module provides comprehensive inventory reporting. Below is a summary of each available report, its filters, and the data columns it displays.
          </Typography>

          {reportInfoData.map((report, index) => (
            <Box
              key={index}
              style={{
                backgroundColor: "#fff",
                borderRadius: 12,
                padding: "18px 20px",
                marginBottom: 16,
                borderLeft: `5px solid ${report.color}`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
              }}
            >
              <Typography
                variant="h6"
                style={{ fontWeight: "bold", color: report.color, marginBottom: 6 }}
              >
                {report.title}
              </Typography>

              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
                {report.description}
              </Typography>

              <Divider style={{ marginBottom: 10 }} />

              {/* Filters */}
              <Typography variant="caption" style={{ fontWeight: "bold", color: "#555", display: "block", marginBottom: 4 }}>
                🔍 FILTERS
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} style={{ marginBottom: 10 }}>
                {report.filters.map((f, i) => (
                  <Chip key={i} label={f} size="small" style={{ backgroundColor: "#E3F2FD", color: "#1565C0", fontWeight: 500 }} />
                ))}
              </Box>

              {/* Columns */}
              <Typography variant="caption" style={{ fontWeight: "bold", color: "#555", display: "block", marginBottom: 4 }}>
                📋 REPORT COLUMNS
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} style={{ marginBottom: 10 }}>
                {report.columns.map((col, i) => (
                  <Chip key={i} label={col} size="small" variant="outlined" style={{ borderColor: report.color, color: report.color }} />
                ))}
              </Box>

              {/* Exports */}
              <Typography variant="caption" style={{ fontWeight: "bold", color: "#555", display: "block", marginBottom: 4 }}>
                📤 EXPORT OPTIONS
              </Typography>
              <Box display="flex" gap={1}>
                {report.exports.map((exp, i) => (
                  <Chip
                    key={i}
                    label={exp}
                    size="small"
                    style={{
                      backgroundColor: exp === "PDF" ? "#FFEBEE" : "#E8F5E9",
                      color: exp === "PDF" ? "#C62828" : "#2E7D32",
                      fontWeight: "bold",
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px", backgroundColor: "#f9f9f9" }}>
          <Button
            onClick={() => setInfoOpen(false)}
            variant="contained"
            style={{ backgroundColor: "#0B1F40", color: "#fff", borderRadius: 20, paddingInline: 28 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
          <Typography variant="h6">Stock Issue Report</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <StockIssue />
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2a-content" id="panel2a-header">
          <Typography variant="h6">Stock Return Report</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <StockReturnReport />
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel3a-content" id="panel3a-header">
          <Typography variant="h6">Stock Summary Report</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <StockSummaryReport />
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Uncomment if needed */}
      {/* <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel4a-content" id="panel4a-header">
          <Typography>Low Stock Report</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <StockSummaryReport />
          </Typography>
        </AccordionDetails>
      </Accordion> */}

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel5a-content" id="panel5a-header">
          <Typography variant="h6">Stock Movement Report</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <StockMovementReport />
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel6a-content" id="panel6a-header">
          <Typography variant="h6">Vendor Purchase Report</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <VendorPurchaseReport />
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Container>
  );
}

export default InventoryReport;